import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { LayerRenderer } from '../models/layer-renderer.model';
import { MapDrawingContext } from '../models/map-drawing-context.model';
import { cyrb53 } from 'src/app/modules/core/services/hasher';
import { LayerMapFeatures } from '../models/layer-map-features.model';
export class TopoTileLayerRenderer implements LayerRenderer {
  addDocuments(drawingContext: MapDrawingContext, layer: Layer, documents: Document[], floor?: string): void {
    // If the layer is currently not visible, ignore the addDocuments call and ensure the tile layer is removed
    if (!drawingContext.isLayerVisible) {
      this.removeDocuments(drawingContext, layer);
      return;
    }

    // Check that Google maps is initialized
    if (!drawingContext.googleMap?.overlayMapTypes){
      return;
    }

    if (!!!drawingContext.mapFeatures[layer.uId]) {
      drawingContext.mapFeatures[layer.uId] = {};
    }
    const layerFeatures = drawingContext.mapFeatures[layer.uId];

    if (!!!layerFeatures[layer.uId]) {
      layerFeatures[layer.uId] = {
        markers: [],
        polygons: [],
        polylines: [],
        circles: [],
        rectangles: [],
        imageOverlays: [],
        hash: null
      };
    }
    const docFeatures = layerFeatures[layer.uId];

    // if we already have overlays for this layer then remove
    // them from the map and redraw them
    if (docFeatures.imageOverlays.length > 0) {
      this.removeDocuments(drawingContext, layer);
    }

    const layerOpacity = this.getValidOpacity(layer.displayOptions?.shapeOpacity);

    const tileSize = layer.displayOptions?.tileSize || 256;

    const layerStyleHash = this.buildTileLayerHash(layer);

    let floorUId: string = null;
    if (!!layer.displayOptions?.floorFilterSourceField) {
      const dataset = drawingContext.mapState.datasetsSet[layer.datasetUId];
      if (!!dataset) {
        // default to floor "1"
        if (!floor){
          floor = "1";
        }
        const field = dataset.schema.fields.find(f => f.systemName === layer.displayOptions.floorFilterSourceField);
        floorUId = field?.picklistValues?.find(p => p.value === floor)?.uId;
      }
    }

    const tileOverlay = this.buildMapTileOverlay(layer.uId, layerOpacity, tileSize, layerStyleHash, floorUId);

    docFeatures.imageOverlays.push(tileOverlay);

    // Remove existing image overlays and re-push them in sort order
    this.addMapOverlaysInTileDisplayOrder(drawingContext, tileOverlay);
  }

  updateDocuments(drawingContext: MapDrawingContext, layer: Layer, documents?: Document[]): void {}

  removeDocuments(drawingContext: MapDrawingContext, layer: Layer, documentUIds?: string[]): void {
    if (!!!drawingContext.mapFeatures[layer.uId]) {
      drawingContext.mapFeatures[layer.uId] = {};
    }
    const layerFeatures = drawingContext.mapFeatures[layer.uId];

    if (!!!layerFeatures[layer.uId]) {
      layerFeatures[layer.uId] = {
        markers: [],
        polygons: [],
        polylines: [],
        circles: [],
        rectangles: [],
        imageOverlays: [],
        hash: null
      };
    }

    this.removeDocument(drawingContext, layer, layer.uId, layerFeatures);
  }

  removeDocument(drawingContext: MapDrawingContext, layer: Layer, uid: string, layerFeatures: LayerMapFeatures): void {
    // Check that Google maps is initialized
    if (!drawingContext.googleMap?.overlayMapTypes){
      return;
    }
    
    const docFeatures = layerFeatures[uid];

    if (!!!docFeatures) {
      return;
    }

    for (let tileOverlay of docFeatures.imageOverlays) {
      const overlayIndex = drawingContext.googleMap.overlayMapTypes.getArray().indexOf(tileOverlay);
      if (overlayIndex >= 0) {
        drawingContext.googleMap.overlayMapTypes.removeAt(overlayIndex);
      }
    }

    docFeatures.imageOverlays = [];
  }

  private buildMapTileOverlay(
    layerUId: string,
    opacity: number,
    tileSize: number,
    layerStyleHash: number,
    floorUId?: string
  ): google.maps.ImageMapType {
    let tileUrlTemplate = '/api/v1/data/maplayers/{layerUId}/tile/{z}/{x}/{y}.png?layerStyleHash={layerStyleHash}';

    const overlay = new google.maps.ImageMapType({
      getTileUrl: (coord, zoom) => {
        let formattedUrl = tileUrlTemplate
          .replace('{layerUId}', layerUId)
          .replace('{z}', zoom.toString())
          .replace('{x}', coord.x.toString())
          .replace('{y}', coord.y.toString())
          .replace('{layerStyleHash}', layerStyleHash.toString());

        if (!!floorUId) {
          formattedUrl += `&floorUId=${floorUId}`;
        }

        return formattedUrl;
      },
      tileSize: new google.maps.Size(tileSize, tileSize),
      opacity,
      name: layerUId
    });

    return overlay;
  }

  private addMapOverlaysInTileDisplayOrder(drawingContext: MapDrawingContext, tileOverlay: google.maps.ImageMapType){
    // Remove existing image overlays and re-push them in sort order
    var tileOverlays = [...drawingContext.googleMap.overlayMapTypes.getArray()];
    drawingContext.googleMap.overlayMapTypes.clear();

    tileOverlays.push(tileOverlay);
    var tileLayerUIds = tileOverlays.map(o => o.name);
    var tileLayers = drawingContext.mapState.layers.filter(l => tileLayerUIds.includes(l.uId));
    tileLayers.sort((a, b) => (a.displayOptions.tileLayerZIndex ?? 0) - (b.displayOptions.tileLayerZIndex ?? 0));

    tileLayers.forEach(l => {
      var overlay = tileOverlays.find(o => o.name == l.uId);
      drawingContext.googleMap.overlayMapTypes.push(overlay);
    })
  }

  buildTileLayerHash(layer: Layer): number {
    const stringToHash = [
      layer.color,
      layer.customMarkerImage,
      layer.iconPath,
      layer.isCustomIcon,
      layer.markerName,
      layer.markerSize,
      layer.displayOptions.floorFilterSourceField,
      JSON.stringify(layer.rule)
    ].join();

    const hash = cyrb53(stringToHash);

    return hash;
  }

  private getValidOpacity(opacity: number): number {
    // Force opacity into a valid range
    if (!opacity && opacity !== 0) {
      opacity = 1;
    }

    if (opacity > 1) {
      opacity = 1;
    } else if (opacity < 0) {
      opacity = 0;
    }

    return opacity;
  }
}
